import { lazy, Suspense } from 'react';
import Navbar from '@/components/Navbar';
import HeroSection from '@/components/HeroSection';
import StatsCounter from '@/components/StatsCounter';
import AboutSection from '@/components/AboutSection';
import Footer from '@/components/Footer';
import ParallaxDots from '@/components/ParallaxDots';
import AmbientBackground from '@/components/AmbientBackground';
import FadeInSection from '@/components/FadeInSection';
import CursorSpotlight from '@/components/CursorSpotlight';
import CookieConsent from '@/components/CookieConsent';
import SEO from '@/components/SEO';
import StructuredData from '@/components/StructuredData';
import ScrollProgress from '@/components/ScrollProgress';
import ScrollToTop from '@/components/ScrollToTop';
import { usePageTracking } from '@/hooks/usePageTracking';

// Lazy-load below-the-fold sections to shrink initial bundle
const WallOfFame = lazy(() => import('@/components/WallOfFame'));
const GalleryPreview = lazy(() => import('@/components/GalleryPreview'));
const VideoGallery = lazy(() => import('@/components/VideoGallery'));
const BeforeAfter = lazy(() => import('@/components/BeforeAfter'));
const WhyUsSection = lazy(() => import('@/components/WhyUsSection'));
const Testimonials = lazy(() => import('@/components/Testimonials'));
const SocialFeed = lazy(() => import('@/components/SocialFeed'));
const ProcessSection = lazy(() => import('@/components/ProcessSection'));
const ContactSection = lazy(() => import('@/components/ContactSection'));
const WorldMapSection = lazy(() => import('@/components/WorldMapSection'));
const ChatBot = lazy(() => import('@/components/ChatBot'));
const FAQSection = lazy(() => import('@/components/FAQSection'));
const VoucherSection = lazy(() => import('@/components/VoucherSection'));
const MaskConfigurator = lazy(() => import('@/components/MaskConfigurator'));

const SectionDivider = () => (
  <div className="relative h-px w-full overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/10 to-transparent blur-sm" />
  </div>
);

const SectionFallback = () => (
  <div className="min-h-[200px] w-full" aria-hidden="true" />
);

const LazySection = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<SectionFallback />}>
    <FadeInSection>{children}</FadeInSection>
  </Suspense>
);

const Index = () => {
  usePageTracking();
  return (
    <main>
      <SEO path="/" />
      <StructuredData />
      <ScrollProgress />
      <ParallaxDots />
      <AmbientBackground />
      <CursorSpotlight />
      <Navbar />
      <HeroSection />
      <SectionDivider />
      <FadeInSection><AboutSection /></FadeInSection>
      <SectionDivider />
      <FadeInSection><StatsCounter /></FadeInSection>
      <SectionDivider />
      <LazySection><GalleryPreview /></LazySection>
      <SectionDivider />
      <LazySection><VideoGallery /></LazySection>
      <SectionDivider />
      <LazySection><BeforeAfter /></LazySection>
      <SectionDivider />
      <LazySection><WallOfFame /></LazySection>
      <SectionDivider />
      <LazySection><VoucherSection /></LazySection>
      <SectionDivider />
      <LazySection><WorldMapSection /></LazySection>
      <SectionDivider />
      <LazySection><ProcessSection /></LazySection>
      <SectionDivider />
      <LazySection><WhyUsSection /></LazySection>
      <SectionDivider />
      <LazySection><MaskConfigurator /></LazySection>
      <SectionDivider />
      <LazySection><Testimonials /></LazySection>
      <SectionDivider />
      <LazySection><FAQSection /></LazySection>
      <SectionDivider />
      <LazySection><SocialFeed /></LazySection>
      <SectionDivider />
      <LazySection><ContactSection /></LazySection>
      <Footer />

      <Suspense fallback={null}>
        <ChatBot />
      </Suspense>
      <ScrollToTop />
      <CookieConsent />
    </main>
  );
};

export default Index;
